import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CloudArrowUpIcon, ArrowLeftIcon, EllipsisVerticalIcon, DocumentArrowDownIcon, TrashIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import WordPressIcon from '../../img/icons/logo-wordpress.svg';
import { IconButton } from '../../ui/atoms';
import './topBar.css';

const TopBar = ({ 
  onGoBack, 
  loading, 
  lastSaved, 
  formatDateUpdated, 
  mode = 'auto',
  showSaveStatus = true, 
  customMiddleContent,
  onManualSave,
  isSaving = false,
  contextType = 'default', // 'article', 'dataset', 'default', etc.
  onDownloadDoc,
  onPublishWebsite,
  onDeleteArticle,
  onArchiveArticle,
  // Dataset-specific props
  onEditName,
  onDeleteDataset,
  showRightActions = true
}) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const resolvedMode = mode === 'auto'
    ? (!showSaveStatus && !showRightActions && !customMiddleContent ? 'back-only' : contextType)
    : mode;
  const isBackOnlyMode = resolvedMode === 'back-only';
  const showCenterStatus = resolvedMode !== 'back-only' && showSaveStatus;
  const showResolvedRightActions = resolvedMode !== 'back-only' && showRightActions;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  return (
    <div
      className={`topbar flex gap-4 justify-between py-1.5 items-center pl-4 pr-4 ${
        isBackOnlyMode ? '' : 'border-b border-neutral-200 bg-white'
      }`}
    >
      <IconButton
        icon={ArrowLeftIcon}
        onClick={onGoBack}
        variant="square"
        iconClassName="h-5 w-5"
        aria-label={t('topBar.goBack')}
      />
      <div className="flex-1 flex justify-center">
        {showCenterStatus ? (
          <div 
            className="topbar-action-button"
            onClick={onManualSave}
          >
            {loading || isSaving ? (
              <div className="animate-spin inline-block w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" role="status">
                <span className="sr-only">{t('topBar.saving')}</span>
              </div>
            ) : (
              <CloudArrowUpIcon className="h-6 w-6 text-primary-500" aria-hidden="true" />
            )}
            <span className="text-sm text-neutral-600">
              {loading || isSaving
                ? t('topBar.updating')
                : t('common.saved', { time: formatDateUpdated(lastSaved) })}
            </span>
          </div>
        ) : customMiddleContent}
      </div>
      {showResolvedRightActions && resolvedMode === 'article' && (
        <div className="flex items-center gap-3">
          {/* Download Button */}
          <button
            onClick={() => onDownloadDoc?.()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <DocumentArrowDownIcon className="h-4 w-4" />
            {t('topBar.download')}
          </button>
          
          {/* Publish on WordPress Button (disabled – soon) */}
          <button
            disabled
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-400 bg-gray-50 border border-gray-200 rounded-lg cursor-not-allowed transition-colors"
          >
            <img src={WordPressIcon} alt="WordPress" className="h-4 w-4 opacity-60" />
            {t('topBar.publishWordPress')}
            <span className="text-xs font-normal text-gray-400 italic">{t('topBar.soon')}</span>
          </button>
          
          {/* Three Dots Menu */}
          <div className="relative" ref={dropdownRef}>
            <IconButton
              icon={EllipsisVerticalIcon}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              variant="square"
              iconClassName="h-5 w-5"
              aria-label={t('topBar.moreOptions')}
            />
            
            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg border border-neutral-200 py-2 z-50">
                <button
                  onClick={() => {
                    onArchiveArticle?.();
                    setIsDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                >
                  <ArchiveBoxIcon className="h-4 w-4" />
                  {t('topBar.archiveArticle')}
                </button>
                
                {/* Divider */}
                <div className="border-t border-gray-200 my-2"></div>
                
                {/* Delete Article */}
                <button
                  onClick={() => {
                    onDeleteArticle?.();
                    setIsDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                >
                  <TrashIcon className="h-4 w-4" />
                  {t('topBar.deleteArticle')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {showResolvedRightActions && resolvedMode === 'dataset' && (
        <div className="relative" ref={dropdownRef}>
          <IconButton
            icon={EllipsisVerticalIcon}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            variant="square"
            iconClassName="h-5 w-5"
            aria-label={t('topBar.moreOptions')}
          />
          
          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg border border-neutral-200 py-2 z-50">
              <button
                onClick={() => {
                  onDeleteDataset?.();
                  setIsDropdownOpen(false);
                }}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
              >
                <TrashIcon className="h-4 w-4" />
                {t('topBar.removeDataset')}
              </button>
            </div>
          )}
        </div>
      )}
      {showResolvedRightActions && resolvedMode !== 'article' && resolvedMode !== 'dataset' && (
        <div className="relative" ref={dropdownRef}>
          <IconButton
            icon={EllipsisVerticalIcon}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            variant="circle"
            iconClassName="h-6 w-6"
            aria-label={t('topBar.moreOptions')}
          />
          
          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg border border-neutral-200 py-2 z-50">
              <div className="px-4 py-2 text-sm text-gray-500">
                {t('topBar.noActions')}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TopBar;
